import { STOCK_WARNING_LIMIT } from '@constants'
import { type AppliedCoupon, type DiscountItem, AppliedStatus, DiscountItemType } from '@hooks-api/types/dataModel/coupon'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { type SubshipInfo } from '@hooks-api/types/dataModel/subship'
import { type Nullable } from '@hooks-api/types/nullable'
import { type TFunction } from 'i18next'
import { isButlerTab } from '../constants/butler-tab'
import { type SpecificAddonCartItem, type CartBaseStore, type CartItem, type FpB2bCartValues, type WpCartValues } from '../types'
import { validateCartItem } from '../utils/validateCartItem'

const calculateRemainingStock = (stock: number, quantity: number) => Math.max(0, stock - quantity)
/** 依據庫存與購買上限，取得可用數量 */
const capQuantity = (stock: number, quantity: number, purchaseLimit?: number | null) => Math.min(quantity, stock, purchaseLimit ?? Infinity)

const findCartItemIndex = (items: CartItem[], item: CartItem) => item.is_addon_v2
  ? items.findIndex(({ cartItemId, is_addon_v2 }) => cartItemId === item.cartItemId && is_addon_v2)
  : items.findIndex(({ cartItemId, is_addon_v2 }) => cartItemId === item.cartItemId && !is_addon_v2)

/**
 * 單品加價購商品的唯一 key 由 cartItemId + addon_main_product_id 組成
 * 因為同一個加購商品可以被添加到不同的主商品下
 */
const findSpecificAddonItemIndex = (items: SpecificAddonCartItem[], item: SpecificAddonCartItem) =>
  items.findIndex(
    existing => existing.cartItemId === item.cartItemId
      && existing.addon_main_product_id === item.addon_main_product_id,
  )

/**
 * 更新購物車項目的通用邏輯
 * @param items 當前購物車項目陣列
 * @param item 要更新的項目
 * @param mode 'add' 累加數量 | 'upsert' 覆蓋更新
 * @returns 更新後的陣列
 */
const updateCartItemInArray = (
  items: CartItem[],
  item: CartItem,
  mode: 'add' | 'upsert',
): CartItem[] => {
  const nextItems = [...items]
  const findIndex = findCartItemIndex(items, item)

  if (findIndex === -1) {
    // 新增項目
    nextItems.push({
      ...item,
      butler: { ...item.butler },
    })
  }
  else {
    if (!nextItems[findIndex]) return items
    nextItems[findIndex] = {
      ...nextItems[findIndex],
      ...(mode === 'upsert' ? item : {}),
      quantity: mode === 'add'
        ? nextItems[findIndex].quantity + item.quantity
        : item.quantity,
      butler: {
        ...nextItems[findIndex].butler,
        ...item.butler,
      },
    }
  }

  return mode === 'upsert' ? nextItems.filter(i => i.quantity > 0) : nextItems
}

export const addCartItem = (prevState: CartBaseStore, item: CartItem): CartBaseStore => {
  if (item.is_addon_v2) {
    const nextAddonItems = updateCartItemInArray(prevState.addonItems || [], item, 'add')
    return { ...prevState, addonItems: nextAddonItems }
  }
  const nextItems = updateCartItemInArray(prevState.items, item, 'add')
  return { ...prevState, items: nextItems }
}

/**
 * 生成單品加價購的唯一 key
 * @param mainProductId 主商品 ID (addon_main_product_id)
 * @param cartItemId 加購商品 ID (cartItemId，等同於 variation_id)
 */
export const getSpecificAddonKey = (mainProductId: number, cartItemId: string | number) =>
  `${mainProductId}-${cartItemId}`

/**
 * 合併單品加價購項目，確保相同 key 的項目數量被正確累加
 */
const mergeSpecificAddonItems = (items: SpecificAddonCartItem[]): SpecificAddonCartItem[] => {
  const mergedMap = new Map<string, SpecificAddonCartItem>()

  for (const item of items) {
    // TODO: 例外處理
    if (!item.addon_main_product_id) continue
    const key = getSpecificAddonKey(item.addon_main_product_id, item.cartItemId)
    const existing = mergedMap.get(key)

    if (existing) {
      // 已存在，累加數量
      mergedMap.set(key, {
        ...existing,
        quantity: existing.quantity + item.quantity,
        butler: {
          ...existing.butler,
          ...item.butler,
        },
      })
    }
    else {
      // 不存在，新增
      mergedMap.set(key, {
        ...item,
        butler: { ...item.butler },
      })
    }
  }

  return Array.from(mergedMap.values())
}

export const addSpecificAddonItems = (prevState: CartBaseStore, addonProducts: SpecificAddonCartItem[]): CartBaseStore => {
  const { specificAddonItems = [] } = prevState

  // 合併現有項目與新項目，同時處理重複數據
  const allItems = [...specificAddonItems, ...addonProducts]
  const nextSpecificAddonItems = mergeSpecificAddonItems(allItems)

  return {
    ...prevState,
    specificAddonItems: nextSpecificAddonItems,
  }
}

export const deleteCartItem = (prevState: CartBaseStore, item: CartItem): CartBaseStore => {
  const { cartItemId, is_addon_v2 } = item
  const { items, addonItems = [] } = prevState

  // 根據 is_addon_v2 決定要從哪個陣列中刪除
  const nextItems = is_addon_v2 ? items : items.filter(item => item.cartItemId !== cartItemId)
  const nextAddonItems = is_addon_v2 ? addonItems.filter(item => item.cartItemId !== cartItemId) : addonItems

  // 合併剩餘商品來判斷（保持原有邏輯）
  const allRemainingItems = [...nextItems, ...nextAddonItems]
  // 如果刪除後，所有剩下的商品都是加價購，則清空購物車
  const isAllAddon = allRemainingItems.every(item => item.is_addon_v2)

  return {
    ...prevState,
    items: isAllAddon ? [] : nextItems,
    addonItems: isAllAddon ? [] : nextAddonItems,
    specificAddonItems: isAllAddon ? [] : prevState.specificAddonItems,
  }
}

export const deleteSpecificAddonItem = (prevState: CartBaseStore, item: SpecificAddonCartItem): CartBaseStore => {
  const { cartItemId, addon_main_product_id: addon_main_product_id } = item
  const { specificAddonItems = [] } = prevState
  // 使用 cartItemId + addon_main_product_id 作為唯一 key 來刪除
  const nextSpecificAddonItems = specificAddonItems.filter(
    existing => !(existing.cartItemId === cartItemId && existing.addon_main_product_id === addon_main_product_id),
  )

  return {
    ...prevState,
    specificAddonItems: nextSpecificAddonItems,
  }
}

export const deleteSpecificAddonItems = (prevState: CartBaseStore, items: SpecificAddonCartItem[]): CartBaseStore => {
  const { specificAddonItems = [] } = prevState
  // 使用 cartItemId + addon_main_product_id 作為唯一 key 來刪除
  // 過濾掉沒有 addon_main_product_id 的項目
  const keysToDelete = new Set(items
    .filter(item => item.addon_main_product_id !== undefined)
    .map(item => getSpecificAddonKey(item.addon_main_product_id as number, item.cartItemId)),
  )
  const nextSpecificAddonItems = specificAddonItems.filter(
    item => !item.addon_main_product_id || !keysToDelete.has(getSpecificAddonKey(item.addon_main_product_id, item.cartItemId)),
  )
  return { ...prevState, specificAddonItems: nextSpecificAddonItems }
}

export const replaceCartItem = (prevState: CartBaseStore, targetCartItemId: CartItem['cartItemId'], item: CartItem): CartBaseStore => {
  if (item.is_addon_v2) {
    // 處理 addon_v2 商品
    const { addonItems = [] } = prevState
    const originalIndex = addonItems.findIndex(i => i.cartItemId === targetCartItemId)
    if (originalIndex === -1) return prevState

    // Step 1: Delete - 移除目標項目，並過濾與新 item cartItemId 重複的項目
    const afterDelete = addonItems
      .filter((i, index) => i.cartItemId !== item.cartItemId || index === originalIndex)
      .filter(i => i.cartItemId !== targetCartItemId)

    // Step 2: Add - 在原本的 index 位置加入新項目
    const insertIndex = Math.min(originalIndex, afterDelete.length)
    const nextAddonItems = [...afterDelete]
    nextAddonItems.splice(insertIndex, 0, item)

    return { ...prevState, addonItems: nextAddonItems }
  }
  else {
    // 處理一般商品
    const { items, specificAddonItems = [] } = prevState
    const originalIndex = items.findIndex(i => i.cartItemId === targetCartItemId)
    if (originalIndex === -1) return prevState

    // 更新 specificAddonItems 的 addon_main_product_id 引用
    const newAddonMainProductId = item.variation_id
    const hasSpecificAddon = specificAddonItems.some(item => item.addon_main_product_id === targetCartItemId)
    const nextSpecificAddonItems = hasSpecificAddon
      ? specificAddonItems.map((item) => {
        if (item.addon_main_product_id === targetCartItemId) {
          return {
            ...item,
            addon_main_product_id: newAddonMainProductId,
          }
        }
        return item
      })
      : specificAddonItems

    // Step 1: Delete - 移除目標項目，並過濾與新 item cartItemId 重複的項目
    const afterDelete = items
      .filter((i, index) => i.cartItemId !== item.cartItemId || index === originalIndex)
      .filter(i => i.cartItemId !== targetCartItemId)

    // Step 2: Add - 在原本的 index 位置加入新項目
    const insertIndex = Math.min(originalIndex, afterDelete.length)
    const nextItems = [...afterDelete]
    nextItems.splice(insertIndex, 0, item)

    return { ...prevState, items: nextItems, specificAddonItems: nextSpecificAddonItems }
  }
}

export const getCartItemById = (state: CartBaseStore, cartItemId: CartItem['cartItemId']): CartItem | undefined => {
  // 先在一般商品中找
  const itemsIndex = state.items.findIndex(cartItem => cartItem.cartItemId === cartItemId)
  if (itemsIndex !== -1) return state.items[itemsIndex]

  // 再在 addon 商品中找
  const addonItemsIndex = (state.addonItems || []).findIndex(cartItem => cartItem.cartItemId === cartItemId)
  if (addonItemsIndex !== -1) return (state.addonItems || [])[addonItemsIndex]

  // 再在 specific addon 商品中找
  const specificAddonItemsIndex = (state.specificAddonItems || []).findIndex(cartItem => cartItem.cartItemId === cartItemId)
  if (specificAddonItemsIndex !== -1) return (state.specificAddonItems || [])[specificAddonItemsIndex]

  return undefined
}

/**
 * 處理購物車商品超賣情況：
 * 若商品數量超過庫存，會優先減少加價購商品的數量，若仍超過則調整一般商品數量至庫存上限。
 */
export const upsertCartItemForUnderstocking = (prevState: CartBaseStore, cartItems?: {
  cartItemId: CartItem['cartItemId']
  stock: CartItem['stock']
  quantity: CartItem['quantity']
  delivery_class: CartItem['delivery_class']
  subship_info?: {
    quantity_per_time: SubshipInfo['quantity_per_time']
  }
}[]): CartBaseStore => {
  const { items = [], addonItems = [], specificAddonItems = [] } = prevState
  const nextItems = [...items]
  const nextAddonItems = [...addonItems]
  const nextSpecificAddonItems = [...specificAddonItems]
  const stockMap = cartItems?.reduce((acc, item) => {
    acc[item.cartItemId] = item.stock
    return acc
  }, {} as Record<CartItem['cartItemId'], CartItem['stock']>)

  const addonItemsMap = nextAddonItems.reduce((acc, addonItem, index) => {
    if (addonItem?.cartItemId) acc.set(addonItem.cartItemId, index)
    return acc
  }, new Map<CartItem['cartItemId'], number>())

  const specificAddonItemsMap = nextSpecificAddonItems.reduce((acc, specificAddonItem) => {
    if (specificAddonItem.cartItemId) acc.add(specificAddonItem.cartItemId)
    return acc
  }, new Set<CartItem['cartItemId']>())

  /**
   * 以單一商品為維度，遍歷 nextItems(一般商品, 全館加價購)，找到 quantity > stock 的商品
   *
   * 1. 調整一般商品數量
   * 2. 處理單品加價購商品：單品加價購可能會有和一般商品相同的 cartItemId，需要遍歷 nextSpecificAddonItems 找到對應的單品加價購商品，並調整數量
   * 3. 處理全館加價購商品：全館加價購可能會有和一般商品相同的 cartItemId，需要遍歷 nextAddonItems 找到對應的全館加價購商品，並調整數量
   */
  nextItems?.forEach((regularItem, index) => {
    const regularItemQuantity = regularItem.quantity
    const stock = stockMap?.[regularItem.cartItemId] ?? 0

    const shouldDistributeSpecificAddonItem = specificAddonItemsMap.has(regularItem.cartItemId)

    let remainingStock = stock

    // 調整一般商品數量
    nextItems[index] = { ...regularItem, quantity: Math.min(regularItemQuantity, remainingStock), stock }

    remainingStock = calculateRemainingStock(remainingStock, regularItemQuantity)

    // 處理單品加價購商品
    if (shouldDistributeSpecificAddonItem) {
      nextSpecificAddonItems.forEach((specificAddonItem, index) => {
        if (specificAddonItem.cartItemId === regularItem.cartItemId) {
          const specificAddonItemQuantity = capQuantity(remainingStock, specificAddonItem.quantity, specificAddonItem.addon_purchase_limit)
          nextSpecificAddonItems[index] = { ...specificAddonItem, quantity: specificAddonItemQuantity, stock }
          remainingStock = calculateRemainingStock(remainingStock, specificAddonItemQuantity)
        }
      })
    }

    const addonItemIndex = addonItemsMap.get(regularItem.cartItemId)
    const shouldDistributeAddonItem = addonItemIndex !== -1 && addonItemIndex !== undefined
    // 處理全館加價購商品
    if (shouldDistributeAddonItem) {
      const addonItem = nextAddonItems[addonItemIndex]
      if (addonItem) {
        const addonItemQuantity = capQuantity(remainingStock, addonItem.quantity, addonItem.addon_purchase_limit)
        nextAddonItems[addonItemIndex] = { ...addonItem, quantity: addonItemQuantity, stock }
        remainingStock = calculateRemainingStock(remainingStock, addonItemQuantity)
      }
    }
  })

  const independentItemStockMap = { ...stockMap }

  // 檢查單品加價購商品庫存（會在這邊被調整庫存的是因為沒有和一般商品重複
  const cappedSpecificAddonItems = nextSpecificAddonItems
    ?.map((specificAddonItem) => {
      const specificAddonItemStock = independentItemStockMap?.[specificAddonItem.cartItemId] ?? 0
      const specificAddonItemQuantity = capQuantity(specificAddonItemStock, specificAddonItem.quantity, specificAddonItem.addon_purchase_limit)
      independentItemStockMap[specificAddonItem.cartItemId] = calculateRemainingStock(specificAddonItemStock, specificAddonItemQuantity)
      return { ...specificAddonItem, quantity: specificAddonItemQuantity, stock: specificAddonItemStock }
    })

  // 檢查全館加價購商品庫存（會在這邊被調整庫存的是因為沒有和一般商品重複
  const cappedAddonItems = nextAddonItems
    ?.map((addonItem) => {
      const remainingItemStock = independentItemStockMap?.[addonItem.cartItemId] ?? 0
      const addonItemQuantity = capQuantity(remainingItemStock, addonItem.quantity, addonItem.addon_purchase_limit)
      independentItemStockMap[addonItem.cartItemId] = calculateRemainingStock(remainingItemStock, addonItemQuantity)
      return { ...addonItem, quantity: addonItemQuantity, stock: remainingItemStock }
    })

  return {
    ...prevState,
    items: nextItems,
    addonItems: cappedAddonItems,
    specificAddonItems: cappedSpecificAddonItems,
  }
}

/**
 * Update or Insert cart Item
 */
export const upsertCartItem = (prevState: CartBaseStore, item: CartItem): CartBaseStore => {
  if (item.is_addon_v2) {
    const nextAddonItems = updateCartItemInArray(prevState.addonItems || [], item, 'upsert')
    return { ...prevState, addonItems: nextAddonItems }
  }
  const nextItems = updateCartItemInArray(prevState.items, item, 'upsert')
  return { ...prevState, items: nextItems }
}

export const upsertSpecificAddonItem = (prevState: CartBaseStore, item: SpecificAddonCartItem): CartBaseStore => {
  const { specificAddonItems = [] } = prevState
  const nextSpecificAddonItems = [...specificAddonItems]
  // 使用 cartItemId + addon_main_product_id 作為唯一 key 來查找
  const findIndex = findSpecificAddonItemIndex(specificAddonItems, item)
  // if not found, add new item else update item
  if (findIndex === -1) {
    nextSpecificAddonItems.push({ ...item, butler: { ...item.butler } })
  }
  else {
    if (!nextSpecificAddonItems[findIndex]) return prevState
    nextSpecificAddonItems[findIndex] = {
      ...nextSpecificAddonItems[findIndex],
      ...item,
      butler: { ...nextSpecificAddonItems[findIndex].butler, ...item.butler },
    }
  }
  return { ...prevState, specificAddonItems: nextSpecificAddonItems.filter(item => item.quantity > 0) }
}

export const updateCartItems = (prevState: CartBaseStore, items: CartItem[] | SpecificAddonCartItem[]): CartBaseStore => {
  // 分離一般商品和 addon_v2 商品
  const regularItems = items.filter(item => !item.is_addon_v2)
  const addonItems = items.filter(item => item.is_addon_v2 && !item.addon_main_product_id)
  const specificAddonItems = items.filter(item => item.is_addon_v2 && item.addon_main_product_id)

  return {
    ...prevState,
    items: regularItems,
    addonItems,
    specificAddonItems,
  }
}

export const getIsCartEmpty = (state: CartBaseStore) => state.items.length === 0 && (state.addonItems || []).length === 0

/** 計算購物車商品總數量 */
export const getCartItemTotalQuantity = (state: CartBaseStore): number => {
  const itemsQuantity = state.items.reduce((sum, item) => sum + item.quantity, 0)
  const addonQuantity = (state.addonItems || []).reduce((sum, item) => sum + item.quantity, 0)
  return itemsQuantity + addonQuantity
}

/**
 * Butler 相關
 */

export const updateCartItemButlerIsDeleted = (prevState: CartBaseStore, item: CartItem, isDeleted: boolean): CartBaseStore => {
  const { cartItemId } = item

  if (item.is_addon_v2) {
    // 處理 addon_v2 商品
    const next = [...(prevState.addonItems || [])]
    const findIndex = next.findIndex(item => item.cartItemId === cartItemId)
    if (findIndex === -1) return prevState
    if (!next[findIndex]) return prevState
    next[findIndex] = { ...next[findIndex], butler: { ...next[findIndex].butler, isDeleted } }
    return { ...prevState, addonItems: next }
  }
  else {
    // 處理一般商品
    const next = [...prevState.items]
    const findIndex = next.findIndex(item => item.cartItemId === cartItemId)
    if (findIndex === -1) return prevState
    if (!next[findIndex]) return prevState
    next[findIndex] = { ...next[findIndex], butler: { ...next[findIndex].butler, isDeleted } }
    return { ...prevState, items: next }
  }
}

export const updateCartItemButlerChecked = (prevState: CartBaseStore, item: CartItem, checked: boolean): CartBaseStore => {
  const { cartItemId } = item

  if (item.is_addon_v2) {
    // 處理 addon_v2 商品
    const next = [...(prevState.addonItems || [])]
    const findIndex = next.findIndex(item => item.cartItemId === cartItemId)
    if (findIndex === -1) return prevState
    if (!next[findIndex]) return prevState
    next[findIndex] = { ...next[findIndex], butler: { ...next[findIndex].butler, checked } }
    return { ...prevState, addonItems: next }
  }
  else {
    // 處理一般商品
    const { items } = prevState
    const next = [...items]
    const findIndex = next.findIndex(item => item.cartItemId === cartItemId)
    if (findIndex === -1) return prevState
    if (!next[findIndex]) return prevState
    next[findIndex] = { ...next[findIndex], butler: { ...next[findIndex].butler, checked } }
    return { ...prevState, items: next }
  }
}

export const updateAllCartItemsButlerChecked = (prevState: CartBaseStore, checked: boolean): CartBaseStore => {
  const { items, addonItems = [] } = prevState
  const nextItems = items.map(item => ({ ...item, butler: { ...item.butler, checked } }))
  const nextAddonItems = addonItems.map(item => ({ ...item, butler: { ...item.butler, checked } }))
  return { ...prevState, items: nextItems, addonItems: nextAddonItems }
}

export const shouldSyncCartStoreFromSession = (
  cartItems?: Nullable<CartItem[]>,
) => {
  if (!cartItems) return false
  const isSessionItemsInValid = cartItems.some(item => !validateCartItem(item))
  if (isSessionItemsInValid) return false
  return true
}

export const getDiscountTotalWithoutShippingCoupon = (discountItems: DiscountItem[]) => {
  const filteredDiscountItems = discountItems?.filter(item => item.type !== DiscountItemType.SHIPPING_COUPON) ?? []
  const discountAmount = filteredDiscountItems.reduce((acc, item) => acc + item.discount_amount, 0)
  return discountAmount
}

/** NOTE: 訂單層級的優惠合計不應該包含「點數/好友推薦」以及「運費優惠」的優惠顯示，前端需自行過濾 */
export const getFilteredDiscountTotal = (discountItems: DiscountItem[]) => {
  const filteredDiscountItems = discountItems?.filter(item => (
    item.type !== DiscountItemType.REFERRAL_CODE
    && item.type !== DiscountItemType.REWARD_POINT
    && item.type !== DiscountItemType.SHIPPING_COUPON
  )) ?? []
  const discountAmount = filteredDiscountItems.reduce((acc, item) => acc + item.discount_amount, 0)
  return discountAmount
}

const getSyncedNextCartItems = (
  cartItemsFromPrevState: CartItem[],
  cartItemsFromSessionCartValues: CartItem[],
  skusFromSessionOrderItems: string[],
) => {
  const mergedItemSkus = [...new Set(
    [
      ...cartItemsFromSessionCartValues.map(item => item.sku),
      ...skusFromSessionOrderItems,
    ])]
  const mergedCartItemMap = [
    ...cartItemsFromPrevState.map(item => ({ sku: item.sku, item })),
    ...cartItemsFromSessionCartValues.map(item => ({ sku: item.sku, item })),
  ].reduce<Map<string, CartItem>>((acc, value) => {
    acc.set(value.sku, value.item)
    return acc
  }, new Map())
  const nextItems = mergedItemSkus.map(sku => mergedCartItemMap.get(sku)).filter(item => item !== undefined && validateCartItem(item))
  return nextItems
}

/**
 * 如果符合 shouldSyncWpCartStoreFromECommerceSession 的規則就套用 session 來的資料，否則維持原本使用者的資料
 * TODO: 目前還未同步所有資料，但理論上可以同步所有資料，之後可嘗試
 */
export const syncWpCartStoreFromECommerceSession = (
  prevState: WpCartValues,
  sessionCartValues: Nullable<WpCartValues>,
  sessionOrderItemSkus: Nullable<string[]>,
): WpCartValues => {
  if (!sessionCartValues || !sessionOrderItemSkus) return prevState
  const nextItems = getSyncedNextCartItems(
    prevState.cart.items,
    sessionCartValues.cart.items,
    sessionOrderItemSkus,
  )
  return {
    ...prevState,
    cart: {
      ...prevState.cart,
      items: nextItems,
    },
    billing: {
      ...prevState.billing,
      billingCountry: sessionCartValues.billing.billingCountry,
    },
    shipping: {
      ...prevState.shipping,
      appliedShippingMethodId: sessionCartValues.shipping.appliedShippingMethodId,
    },
    coupon: {
      ...prevState.coupon,
      manualInputCouponIds: sessionCartValues.coupon.manualInputCouponIds || [],
    },
    rewardPoints: {
      ...prevState.rewardPoints,
      userInputRewardPoints: sessionCartValues.rewardPoints.userInputRewardPoints,
      isUserAppliedRewardPoints: sessionCartValues.rewardPoints.isUserAppliedRewardPoints,
    },
  }
}

export const syncFpB2bCartStoreFromECommerceSession = (
  prevState: FpB2bCartValues,
  sessionCartValues: Nullable<FpB2bCartValues>,
  sessionOrderItemSkus: Nullable<string[]>,
): FpB2bCartValues => {
  if (!sessionCartValues || !sessionOrderItemSkus) return prevState
  const nextItems = getSyncedNextCartItems(
    prevState.cart.items,
    sessionCartValues.cart.items,
    sessionOrderItemSkus,
  )
  return {
    ...prevState,
    butler: {
      ...prevState.butler,
      ...sessionCartValues.butler,
      // 後端 session 可能還留著已移除的分頁（舊的文字辨識分頁），照單全收會讓分頁選不到任何一頁
      selectedTab: isButlerTab(sessionCartValues.butler.selectedTab) ? sessionCartValues.butler.selectedTab : prevState.butler.selectedTab,
    },
    cart: {
      ...prevState.cart,
      items: nextItems,
    },
    shipping: {
      ...prevState.shipping,
      appliedShippingMethodId: sessionCartValues.shipping.appliedShippingMethodId,
    },
    coupon: {
      ...prevState.coupon,
      manualInputCouponIds: sessionCartValues.coupon.manualInputCouponIds || [],
    },
    merchant: {
      ...prevState.merchant,
      index: sessionCartValues.merchant.index ?? null,
      billingCountry: sessionCartValues.merchant.billingCountry,
    },
  }
}

export const getCartListItemHelperText = (
  stock: number,
  quantity: number,
  isAdjustedToMaxQuantity: boolean,
  t: TFunction,
  addonV2PurchaseLimit?: number,
  productLifeLimit?: number,
  parentProductPurchasedLimit?: number | null,
  isParentLimitExceeded?: boolean,
) => {
  /** 母商品加價購超過購買上限 */
  if (isParentLimitExceeded && parentProductPurchasedLimit) {
    return t('cartUi.exceedsParentProductPurchaseLimit', {
      limit: parentProductPurchasedLimit,
      defaultValue: `超過購買上限 (${parentProductPurchasedLimit})`,
    })
  }
  /** 調整為購買上限 */
  if (isAdjustedToMaxQuantity) return t('cartUi.qtyUpdatedToMaxAllowed', { defaultValue: '調整為購買上限' })
  // 避免 quantity 為負值
  const availableQuantity = Math.max(stock - quantity, 0)
  // WP 後台終生限購數量
  if (productLifeLimit && quantity >= productLifeLimit) return t('cartUi.purchaseLimit', { purchaseLimit: productLifeLimit, defaultValue: `每人限購 ${productLifeLimit} 個` })
  /** 已達購買上限 */
  if (availableQuantity === 0) return t('cartUi.limitReached', { defaultValue: '已達庫存上限' })
  if (addonV2PurchaseLimit && quantity >= addonV2PurchaseLimit) return `${t('cartUi.limitOfPurchase', { defaultValue: '此規格限購' })}：${addonV2PurchaseLimit}`
  if (availableQuantity <= STOCK_WARNING_LIMIT) return `${t('cartUi.remainQuantity', { defaultValue: '剩餘數量' })} ${availableQuantity}`
  return null
}

export interface GetStockHelperTextParams {
  /** 庫存數量 */
  stock: number
  /** 當前數量 */
  quantity: number
  /** i18n 翻譯函數 */
  t: TFunction
  /** 終生限購數量 */
  productLifeLimit?: number
  /** 選項限購數量（單品加價購/加價購） */
  purchaseLimit?: number
  /** 是否已售完 */
  isOutOfStock?: boolean
  /** 是否已調整為購買上限 */
  isAdjustedToMaxQuantity?: boolean
}

/**
 * 通用庫存提示文字
 * 優先順序：已售完/庫存不足 > 已達庫存上限 > 終生限購 > 選項限購 > 庫存低於警告閾值
 */
export const getStockHelperText = (params: GetStockHelperTextParams): string | undefined => {
  const { stock, quantity, t, productLifeLimit, purchaseLimit, isOutOfStock, isAdjustedToMaxQuantity } = params
  const availableQuantity = Math.max(stock - quantity, 0)

  // 優先級 1：已售完（庫存 <= 0 或 isOutOfStock）
  if (isOutOfStock || stock <= 0) {
    return String(t('cartUi.soldout', { defaultValue: '已售完' }))
  }
  /** 調整為購買上限, TODO: 等待庫存後台修復後測試 */
  if (isAdjustedToMaxQuantity) return String(t('cartUi.qtyUpdatedToMaxAllowed', { defaultValue: '調整為購買上限' }))
  // 優先級 2：終生限購
  if (productLifeLimit && quantity >= productLifeLimit) {
    return String(t('cartUi.purchaseLimit', { purchaseLimit: productLifeLimit, defaultValue: `每人限購 ${productLifeLimit} 個` }))
  }
  // 優先級 3：選項限購
  if (purchaseLimit && quantity >= purchaseLimit) {
    return `${t('cartUi.limitOfPurchase', { defaultValue: '此選項限購' })}：${purchaseLimit}`
  }
  // 優先級 4：已達庫存上限（僅當有選擇數量且剩餘為 0 時）
  if (quantity > 0 && availableQuantity === 0) {
    return String(t('cartUi.limitReached', { defaultValue: '已達庫存上限' }))
  }
  // 優先級 5：庫存低於警告閾值
  if (availableQuantity <= STOCK_WARNING_LIMIT) {
    return `${t('cartUi.remainQuantity', { defaultValue: '剩餘數量' })} ${availableQuantity}`
  }
  return undefined
}

export const getUsedCouponCount = (appliedCoupons: AppliedCoupon[]) => {
  const usedCount = appliedCoupons.filter(coupon => coupon.criterions.some(criterion => criterion.applied_status === AppliedStatus.APPLIED)).length
  return usedCount
}

/**
 * 取得不符合條件的贈品或滿額禮
 */
export const getProductIdsWithExcludedShippingClass = (products: ECommerceProduct[], excludedShippingClass?: string[]) => {
  return products
    .filter((product) => {
      const shippingClass = product?.shipping_class || ''
      return excludedShippingClass?.includes(shippingClass)
    })
    .map(product => ({
      productTitle: product?.title,
    }))
}
